import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { UNIT_SYSTEM_IMPERIAL, type UnitSystem } from "../../../types";
import type { SubstructureForSettings } from "../../../http/deckDesigner/types/substructures";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import {
  centimetersToFractionalInches,
  kilonewtonsPerSquareMeterToPsf,
  kilonewtonsToPoundsForce,
  metersToFeetAndInches,
  millimetersToFractionalInches,
  squareMetersToSquareFeet,
} from "./imperialConversions";

export type MetricLengthUnit = "mm" | "cm" | "m";

// How measurements are shown: values are always metric, imperial is display only. Each caller passes
// the text it already shows in metric, so the metric output stays exactly as it was.
export type MeasurementFormat = {
  isImperial: boolean;
  length: (value: number, unit: MetricLengthUnit, metricText: string) => string;
  area: (squareMeters: number, metricText: string) => string;
  liveLoad: (kilonewtonsPerSquareMeter: number, metricText: string) => string;
  force: (kilonewtons: number, metricText: string) => string;
};

const toImperialLength = (value: number, unit: MetricLengthUnit): string => {
  switch (unit) {
    case "m":
      return metersToFeetAndInches(value);
    case "cm":
      return centimetersToFractionalInches(value);
    case "mm":
      return millimetersToFractionalInches(value);
  }
};

export const createMeasurementFormat = (unitSystem: UnitSystem | null | undefined): MeasurementFormat => {
  const isImperial = unitSystem === UNIT_SYSTEM_IMPERIAL;

  return {
    isImperial,
    length: (value, unit, metricText) => (isImperial ? toImperialLength(value, unit) : metricText),
    area: (squareMeters, metricText) => (isImperial ? `${squareMetersToSquareFeet(squareMeters)} ft²` : metricText),
    liveLoad: (kilonewtonsPerSquareMeter, metricText) =>
      isImperial ? `${kilonewtonsPerSquareMeterToPsf(kilonewtonsPerSquareMeter)} psf` : metricText,
    force: (kilonewtons, metricText) => (isImperial ? `${kilonewtonsToPoundsForce(kilonewtons)} lbf` : metricText),
  };
};

// For components inside the deck designer's FormProvider.
export const useMeasurementFormat = (): MeasurementFormat => {
  const unitSystem = useWatch<DeckDesignerConfigurationFormValues, "unitSystem">({ name: "unitSystem" });
  return useMemo(() => createMeasurementFormat(unitSystem), [unitSystem]);
};

// A substructure section: its item, or its size when it has none.
export const formatSubstructureLabel = (
  option: SubstructureForSettings,
  measurementFormat: MeasurementFormat,
  common: { unitMm: string; thickness: string },
): string => {
  if (option.item) {
    return `${option.item.code} - ${option.item.description}`;
  }

  const height = measurementFormat.length(option.height, "mm", `${option.height} ${common.unitMm}`);
  if (option.base == null) {
    return `${common.thickness}: ${height}`;
  }

  const base = measurementFormat.length(option.base, "mm", `${option.base}${common.unitMm}`);
  return `${base} x ${height}`;
};

// The short form, for a select's chosen value: just the item code when there is one.
export const formatSubstructureOptionLabel = (
  option: SubstructureForSettings,
  measurementFormat: MeasurementFormat,
  common: { unitMm: string; thickness: string },
): string => (option.item ? option.item.code : formatSubstructureLabel(option, measurementFormat, common));
